/**
 * Wizard de novo envio para assinatura.
 *
 *   1. Seleção          → titular do documento e quais documentos
 *   2. Signatários e envio → quem assina, por onde recebe, como assina
 *   3. Revisão final    → confere e envia
 *
 * O envelope só existe no banco quando a clínica conclui a etapa 3. Até lá o
 * rascunho vive no estado desta tela — um envelope pela metade no banco
 * poderia ser enviado por engano.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/SearchRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/PhoneIphoneRounded';
import DrawIcon from '@mui/icons-material/GestureRounded';
import KeyboardIcon from '@mui/icons-material/KeyboardOutlined';
import VerifiedIcon from '@mui/icons-material/VerifiedUserOutlined';
import PasswordIcon from '@mui/icons-material/PasswordRounded';

import DentalPatientPicker, {
  type DentalPatientOption,
} from '../odontology/DentalPatientPicker';
import ProfessionalAutocomplete, {
  type ProfessionalOption,
} from '../ProfessionalAutocomplete';
import SignerFormDialog, { type SignerDraft } from './SignerFormDialog';
import { createEnvelope, listContractTemplates, sendEnvelope } from '../../services/signatureService';
import { useAuth } from '../../contexts/AuthContext';
import {
  CONTRACT_CATEGORY_LABELS,
  DELIVERY_METHOD_HINTS,
  DELIVERY_METHOD_LABELS,
  SIGNATURE_METHOD_HINTS,
  SIGNATURE_METHOD_LABELS,
  SIGNER_ROLE_LABELS,
} from '../../constants/signature';
import type {
  ContractTemplate,
  DeliveryMethod,
  EnvelopeDetail,
  SignatureMethod,
} from '../../types/signature';

const STEPS = ['Seleção', 'Signatários e envio', 'Revisão final'];

const PROFESSIONAL_VAR_RE = /\{\{(?:#)?\s*(profissional|registro_profissional|especialidade)\s*\}\}/;
const PROCEDURE_VAR_RE = /\{\{(?:#)?\s*procedimento\s*\}\}/;

function templateNeedsProfessional(template: ContractTemplate): boolean {
  return PROFESSIONAL_VAR_RE.test(template.body_html || '');
}

function templateNeedsProcedure(template: ContractTemplate): boolean {
  return PROCEDURE_VAR_RE.test(template.body_html || '');
}

interface NewSignatureWizardProps {
  /** Pré-seleciona o titular quando o wizard abre pelo painel do paciente. */
  initialPatient?: DentalPatientOption | null;
  /** Pré-seleciona o profissional responsável (variáveis do documento). */
  initialProfessional?: ProfessionalOption | null;
  onCancel: () => void;
  onCreated: (detail: EnvelopeDetail, sent: boolean) => void;
}

/** Trilha de etapas: substitui o Stepper do MUI para ficar legível no mobile. */
function StepTrail({ active }: { active: number }) {
  return (
    <Stack
      direction="row"
      spacing={{ xs: 1, sm: 2 }}
      alignItems="center"
      justifyContent="center"
      sx={{ mb: 4, flexWrap: 'wrap', rowGap: 1 }}
    >
      {STEPS.map((label, index) => {
        const done = index < active;
        const current = index === active;
        return (
          <Stack key={label} direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: done || current ? '#fff' : 'text.disabled',
                  bgcolor: done ? '#0D9488' : current ? '#0F172A' : 'rgba(148,163,184,0.25)',
                  transition: 'background-color 200ms ease',
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 15 }} /> : index + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: current ? 700 : 500,
                  color: current ? 'text.primary' : done ? '#0D9488' : 'text.disabled',
                  display: { xs: current ? 'block' : 'none', sm: 'block' },
                }}
              >
                {label}
              </Typography>
            </Stack>
            {index < STEPS.length - 1 && (
              <Box
                sx={{
                  width: { xs: 16, sm: 48 },
                  height: 1,
                  bgcolor: done ? '#0D9488' : 'rgba(148,163,184,0.35)',
                }}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

/** Cartão de opção selecionável (método de envio e forma de assinatura). */
function OptionCard({
  selected,
  disabled,
  icon,
  title,
  subtitle,
  onClick,
  multi = false,
}: {
  selected: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  multi?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      onClick={disabled ? undefined : onClick}
      sx={{
        flex: '1 1 200px',
        minWidth: 190,
        p: 1.75,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        borderColor: selected ? '#0D9488' : 'divider',
        borderWidth: selected ? 2 : 1,
        bgcolor: selected ? 'rgba(13,148,136,0.05)' : 'background.paper',
        transition: 'border-color 160ms ease, background-color 160ms ease',
        '&:hover': disabled ? {} : { borderColor: selected ? '#0D9488' : '#94A3B8' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        {multi ? (
          <Checkbox
            checked={selected}
            disabled={disabled}
            size="small"
            sx={{ p: 0, mt: 0.2, color: '#94A3B8', '&.Mui-checked': { color: '#0D9488' } }}
          />
        ) : (
          <Box
            sx={{
              width: 16,
              height: 16,
              mt: 0.35,
              borderRadius: '50%',
              flexShrink: 0,
              border: '2px solid',
              borderColor: selected ? '#0D9488' : '#CBD5E1',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {selected && (
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#0D9488' }} />
            )}
          </Box>
        )}

        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ color: selected ? '#0D9488' : 'text.disabled', display: 'flex' }}>{icon}</Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.35, lineHeight: 1.45 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function NewSignatureWizard({
  initialPatient = null,
  initialProfessional = null,
  onCancel,
  onCreated,
}: NewSignatureWizardProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Etapa 1
  const [patient, setPatient] = useState<DentalPatientOption | null>(initialPatient);
  const [professional, setProfessional] = useState<ProfessionalOption | null>(
    initialProfessional
      || (user?.professional_id
        ? { id: user.professional_id, full_name: user.full_name }
        : null),
  );
  const [procedureName, setProcedureName] = useState('');
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);

  // Etapa 2
  const [signers, setSigners] = useState<SignerDraft[]>([]);
  const [signerDialogOpen, setSignerDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [delivery, setDelivery] = useState<DeliveryMethod>('device');
  const [requireAccessCode, setRequireAccessCode] = useState(false);
  const [methods, setMethods] = useState<SignatureMethod[]>(['handwritten']);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const data = await listContractTemplates({
          professional_id: professional?.id || undefined,
        });
        if (!cancelled) setTemplates(data);
      } catch {
        if (!cancelled) setError('Não foi possível carregar os modelos de documento.');
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [professional?.id]);

  // O titular é o signatário natural. Pré-carregar evita que a clínica redigite
  // o que já está no cadastro — e o CPF vindo do prontuário é mais confiável.
  useEffect(() => {
    if (patient && signers.length === 0) {
      setSigners([
        {
          name: patient.full_name,
          cpf: patient.cpf || undefined,
          phone: patient.phone || undefined,
          sign_role: 'patient',
          patient_id: patient.id,
        },
      ]);
    }
  }, [patient, signers.length]);

  const selectedTemplates = useMemo(
    () => templates.filter((t) => selectedTemplateIds.includes(t.id)),
    [templates, selectedTemplateIds],
  );

  const requiresProfessional = useMemo(
    () => selectedTemplates.some(templateNeedsProfessional),
    [selectedTemplates],
  );

  const requiresProcedure = useMemo(
    () => selectedTemplates.some(templateNeedsProcedure),
    [selectedTemplates],
  );

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(term));
  }, [templates, search]);

  // PAdES sela o PDF inteiro: com dois signatários, o segundo invalidaria o
  // selo do primeiro. O backend recusa, então a opção some aqui também.
  const icpDisabled = signers.length > 1;

  useEffect(() => {
    if (icpDisabled && methods.includes('icp')) {
      setMethods((prev) => {
        const next = prev.filter((m) => m !== 'icp');
        return next.length > 0 ? next : ['handwritten'];
      });
    }
  }, [icpDisabled, methods]);

  const toggleTemplate = (id: string) =>
    setSelectedTemplateIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );

  const toggleMethod = (method: SignatureMethod) =>
    setMethods((prev) => {
      if (prev.includes(method)) {
        const next = prev.filter((m) => m !== method);
        return next.length > 0 ? next : prev;
      }
      return [...prev, method];
    });

  const canAdvance = useMemo(() => {
    if (step === 0) {
      if (selectedTemplateIds.length === 0) return false;
      if (requiresProfessional && !professional?.id) return false;
      if (requiresProcedure && !procedureName.trim()) return false;
      return true;
    }
    if (step === 1) {
      if (signers.length === 0) return false;
      if (delivery === 'whatsapp') {
        return signers.every((s) => (s.phone || '').replace(/\D/g, '').length >= 10);
      }
      return true;
    }
    return true;
  }, [step, selectedTemplateIds, signers, delivery, requiresProfessional, professional?.id, requiresProcedure, procedureName]);

  const handleSubmit = async (send: boolean) => {
    setError(null);
    setSaving(true);
    try {
      const extraVars: Record<string, string> = {};
      if (procedureName.trim()) extraVars.procedimento = procedureName.trim();

      const detail = await createEnvelope({
        patient_id: patient?.id || null,
        professional_id: professional?.id || null,
        extra_vars: Object.keys(extraVars).length > 0 ? extraVars : undefined,
        delivery_method: delivery,
        require_access_code: requireAccessCode,
        allowed_signature_methods: methods,
        message: message.trim() || null,
        documents: selectedTemplateIds.map((id) => ({ template_id: id })),
        signers: signers.map((s) => ({ ...s, sign_role: s.sign_role })),
      });

      if (!send) {
        onCreated(detail, false);
        return;
      }

      const sent = await sendEnvelope(detail.envelope.id);
      onCreated(sent, true);
    } catch (e: any) {
      setError(e?.message || 'Não foi possível concluir o envio.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <StepTrail active={step} />

      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, p: { xs: 2, sm: 4 }, maxWidth: 760, mx: 'auto' }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 0 && (
          <Stack spacing={4}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>
                Titular do documento
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                Selecione a pessoa a quem este documento se refere.
              </Typography>
              <DentalPatientPicker value={patient} onChange={setPatient} label="Paciente" />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>
                Profissional responsável
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                {requiresProfessional
                  ? 'Obrigatório para preencher as variáveis {{profissional}}, {{registro_profissional}} e {{especialidade}} nos documentos selecionados.'
                  : 'Opcional. Selecione o profissional do procedimento para preencher as variáveis do documento.'}
              </Typography>
              <ProfessionalAutocomplete
                value={professional}
                onChange={setProfessional}
                label={requiresProfessional ? 'Profissional responsável' : 'Profissional (opcional)'}
                required={requiresProfessional}
                error={requiresProfessional && !professional?.id}
                helperText={
                  requiresProfessional && !professional?.id
                    ? 'Selecione o profissional referente ao procedimento.'
                    : undefined
                }
              />
            </Box>

            {requiresProcedure && (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>
                  Procedimento
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                  Será inserido na variável {'{{procedimento}}'} do documento.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Procedimento"
                  value={procedureName}
                  onChange={(e) => setProcedureName(e.target.value)}
                  placeholder="Ex.: Cirurgia de catarata"
                  required
                  error={!procedureName.trim()}
                  helperText={!procedureName.trim() ? 'Informe o procedimento do documento.' : undefined}
                />
              </Box>
            )}

            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>
                    Modelos de documento
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
                    Selecione abaixo um ou mais modelos.
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar documento"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { xs: '100%', sm: 220 } }}
                />
              </Stack>

              {loadingTemplates && (
                <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!loadingTemplates && filteredTemplates.length === 0 && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Nenhum modelo cadastrado ainda. Crie o primeiro em Clínica → Modelos de contrato.
                </Alert>
              )}

              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {filteredTemplates.map((template) => {
                  const selected = selectedTemplateIds.includes(template.id);
                  return (
                    <Paper
                      key={template.id}
                      variant="outlined"
                      onClick={() => toggleTemplate(template.id)}
                      sx={{
                        flex: '1 1 300px',
                        minWidth: 260,
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        borderColor: selected ? '#0D9488' : 'divider',
                        borderWidth: selected ? 2 : 1,
                        bgcolor: selected ? 'rgba(13,148,136,0.05)' : 'background.paper',
                        '&:hover': { borderColor: selected ? '#0D9488' : '#94A3B8' },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Checkbox
                          checked={selected}
                          size="small"
                          sx={{
                            p: 0,
                            mt: 0.25,
                            color: '#94A3B8',
                            '&.Mui-checked': { color: '#0D9488' },
                          }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.35 }}>
                            {template.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                            {CONTRACT_CATEGORY_LABELS[template.category] || template.category}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 1 && (
          <Stack spacing={4}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Signatários</Typography>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                Selecione as pessoas que irão assinar os documentos.
              </Typography>

              <Stack spacing={1}>
                {signers.map((signer, index) => (
                  <Paper
                    key={`${signer.name}-${index}`}
                    variant="outlined"
                    sx={{ borderRadius: 2, px: 2, py: 1.5 }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{signer.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                          {[
                            signer.cpf,
                            signer.phone,
                            signer.email,
                            `Assina como: ${SIGNER_ROLE_LABELS[signer.sign_role]}`,
                          ]
                            .filter(Boolean)
                            .join('  ·  ')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingIndex(index);
                            setSignerDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setSigners((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingIndex(null);
                  setSignerDialogOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, mt: 1 }}
              >
                Adicionar signatário
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Método de envio</Typography>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                Defina por qual canal o signatário recebe o documento, ou se vai assinar aqui mesmo.
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                <OptionCard
                  selected={delivery === 'whatsapp'}
                  icon={<WhatsAppIcon fontSize="small" />}
                  title={DELIVERY_METHOD_LABELS.whatsapp}
                  subtitle={DELIVERY_METHOD_HINTS.whatsapp}
                  onClick={() => setDelivery('whatsapp')}
                />
                <OptionCard
                  selected={delivery === 'device'}
                  icon={<PhoneIcon fontSize="small" />}
                  title={DELIVERY_METHOD_LABELS.device}
                  subtitle={DELIVERY_METHOD_HINTS.device}
                  onClick={() => setDelivery('device')}
                />
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>
                Autenticação e assinatura
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                Escolha as validações que os signatários devem concluir antes de finalizar.
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                <OptionCard
                  multi
                  selected={methods.includes('handwritten')}
                  icon={<DrawIcon fontSize="small" />}
                  title={SIGNATURE_METHOD_LABELS.handwritten}
                  subtitle={SIGNATURE_METHOD_HINTS.handwritten}
                  onClick={() => toggleMethod('handwritten')}
                />
                <OptionCard
                  multi
                  selected={methods.includes('typed')}
                  icon={<KeyboardIcon fontSize="small" />}
                  title={SIGNATURE_METHOD_LABELS.typed}
                  subtitle={SIGNATURE_METHOD_HINTS.typed}
                  onClick={() => toggleMethod('typed')}
                />
                <OptionCard
                  multi
                  disabled={icpDisabled}
                  selected={methods.includes('icp')}
                  icon={<VerifiedIcon fontSize="small" />}
                  title={SIGNATURE_METHOD_LABELS.icp}
                  subtitle={
                    icpDisabled
                      ? 'Indisponível com mais de um signatário'
                      : SIGNATURE_METHOD_HINTS.icp
                  }
                  onClick={() => toggleMethod('icp')}
                />
              </Stack>

              <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.75, mt: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <PasswordIcon
                      fontSize="small"
                      sx={{ color: requireAccessCode ? '#0D9488' : 'text.disabled' }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Código de acesso</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Código de 6 dígitos por WhatsApp antes de abrir o documento
                      </Typography>
                    </Box>
                  </Stack>
                  <Switch
                    checked={requireAccessCode}
                    onChange={(e) => setRequireAccessCode(e.target.checked)}
                  />
                </Stack>
              </Paper>
            </Box>

            <TextField
              label="Mensagem para o signatário (opcional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="Ex.: Leia com atenção antes de assinar. Qualquer dúvida, fale com a recepção."
            />
          </Stack>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 2 && (
          <Stack spacing={3.5}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>Signatários</Typography>
              <Stack spacing={1}>
                {signers.map((signer, index) => (
                  <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#E2E8F0',
                        color: '#334155',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {signer.name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join('')}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{signer.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {patient && signer.patient_id === patient.id ? 'Titular e signatário' : SIGNER_ROLE_LABELS[signer.sign_role]}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {(patient || professional) && (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>Titular e profissional</Typography>
                <Stack spacing={0.75}>
                  {patient && (
                    <Typography sx={{ fontSize: 14 }}>
                      Titular: <strong>{patient.full_name}</strong>
                    </Typography>
                  )}
                  {professional && (
                    <Typography sx={{ fontSize: 14 }}>
                      Profissional: <strong>{professional.full_name}</strong>
                      {professional.specialty ? ` — ${professional.specialty}` : ''}
                    </Typography>
                  )}
                  {procedureName.trim() && (
                    <Typography sx={{ fontSize: 14 }}>
                      Procedimento: <strong>{procedureName.trim()}</strong>
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>Documentos</Typography>
              <Stack spacing={1}>
                {selectedTemplates.map((template) => (
                  <Stack key={template.id} direction="row" spacing={1.25} alignItems="center">
                    <DescriptionIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: 14 }}>{template.name}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>
                Método de envio, código e assinatura
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  {delivery === 'whatsapp' ? (
                    <WhatsAppIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  ) : (
                    <PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  )}
                  <Typography sx={{ fontSize: 14 }}>
                    Envio: {DELIVERY_METHOD_LABELS[delivery]}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <PasswordIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: 14 }}>
                    Código de acesso: {requireAccessCode ? 'ativo' : 'inativo'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <DrawIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: 14 }}>
                    Assinatura: {methods.map((m) => SIGNATURE_METHOD_LABELS[m].toLowerCase()).join(', ')}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {delivery === 'whatsapp' && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                O link vai pela API oficial, com um template aprovado do tipo "Assinatura de
                documento". Sem esse template na instância da unidade, o envio falha e você pode
                coletar a assinatura pelo QR code.
              </Alert>
            )}
          </Stack>
        )}

        {/* ---------------------------------------------------------------- */}
        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" flexWrap="wrap" rowGap={1}>
          <Button
            onClick={step === 0 ? onCancel : () => setStep((s) => s - 1)}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {step === 0 ? 'Cancelar' : 'Anterior'}
          </Button>

          {step < 2 && (
            <Button
              variant="contained"
              disabled={!canAdvance}
              onClick={() => setStep((s) => s + 1)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
            >
              Continuar
            </Button>
          )}

          {step === 2 && (
            <>
              <Button
                variant="outlined"
                disabled={saving}
                onClick={() => handleSubmit(false)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Salvar como rascunho
              </Button>
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => handleSubmit(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  bgcolor: '#0D9488',
                  '&:hover': { bgcolor: '#0f766e' },
                }}
              >
                {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Enviar para assinatura'}
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      <SignerFormDialog
        open={signerDialogOpen}
        initial={editingIndex !== null ? signers[editingIndex] : null}
        requirePhone={delivery === 'whatsapp'}
        onClose={() => setSignerDialogOpen(false)}
        onSave={(signer) => {
          setSigners((prev) =>
            editingIndex !== null
              ? prev.map((s, i) => (i === editingIndex ? signer : s))
              : [...prev, signer],
          );
          setSignerDialogOpen(false);
        }}
      />
    </Box>
  );
}
